import { Code, ConnectError } from "@bufbuild/connect"
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from "@bufbuild/connect-web"

import api from "../api/api"
import { Asset } from "@/shared/api/protocol-ts/model/dto_asset_pb"
import { AssetContent_Report_Studio_ApplicationStateSnapshot, AssetContent_Report_Studio_Geometry } from "@/shared/api/protocol-ts/model/dto_asset_segmentron_common_pb"
import { getFileSrc } from "../lib"
import { AssetWithDownloadURLs, ReportBundle } from "@/shared/api/protocol-ts/model/dto_common_service_pb"
import { RenderDisplayImageReq } from "@/shared/api/protocol-ts/api/renderer/svc_renderer_pb"
import { CORS_POLICY } from "../config"
import { RendererService } from "@/shared/api/protocol-ts/api/renderer/svc_renderer_connectweb"
import { debug } from "../lib"


type GlobalCollector = {
  Assets: any[],
  Teeth: any[],
  Conditions: any[],
  TeethLandmarks: any[],
  Report: any,
  Patient: any,
  Organization: any,
  Studies: any[],
  User: any,
}

let abortController: AbortController
const runReportDataStream = async (): Promise<GlobalCollector | undefined> => {
  const GLOBAL_COLLECTOR: GlobalCollector = {
    Assets: [],
    Teeth: [],
    Conditions: [],
    TeethLandmarks: [],
    Report: {},
    Patient: {},
    Organization: {},
    Studies: [],
    User: {},
  }



  const reportID = window.location.pathname.match(/\/(reports|preview2d)\/([^\/]+)/)?.[2]

  if (!reportID) {
    throw new Error(
      '[useReportDataStream] analysis_uid are not defined in url params',
    )
  }

  const openReportDataStream = async () => {
    abortController = new AbortController()

    try {
      const reportDataStream = api.report.reportDataStream(
        { ReportID: reportID },
        { signal: abortController.signal },
      )

      for await (const { Update } of reportDataStream) {
        switch (Update.case) {
          case 'HistoricalReport':
            const report = Update.value
            GLOBAL_COLLECTOR.Report = report
            break
          case 'HistoricalPatient': 
            GLOBAL_COLLECTOR.Patient = Update.value
            break
          case 'HistoricalOrganization':
            GLOBAL_COLLECTOR.Organization = Update.value
            break
          case 'HistoricalStudy':
            GLOBAL_COLLECTOR.Studies.push(Update.value)
            break
          case 'HistoricalCurrentUser':
            GLOBAL_COLLECTOR.User = Update.value
            break
          case 'HistoricalAsset':
            const asset = {
              Asset: Update.value,
              DownloadURLs: {},
            }
            const fileIDs = getFileIds(Update.value)
            if (fileIDs?.length) {
              const DownloadURLs: { [key: string]: string } = {}
              fileIDs.forEach(fileID => {
                DownloadURLs[fileID] = getFileSrc(fileID)
              })
              asset.DownloadURLs = DownloadURLs
            } else debug("[useReportDataStream] no fileID for asset", Update.value)
            GLOBAL_COLLECTOR.Assets.push(asset)
            break
          case 'HistoricalTooth':
            GLOBAL_COLLECTOR.Teeth.push(Update.value)
            break
          case 'HistoricalCondition':
            GLOBAL_COLLECTOR.Conditions.push(Update.value)
            break
          case 'HistoricalToothLandmark':
            GLOBAL_COLLECTOR.TeethLandmarks.push(Update.value)
            break
          case 'EndOfHistoricalUsers':
            return GLOBAL_COLLECTOR// finish
          default: {
            break
          }
        }
      }
    } catch (error) {
      if (error instanceof ConnectError) {
        if (error.code === Code.Unauthenticated) {
          debug('error.code === Code.Unauthenticated')
        }
      }
    }
  }

  return await openReportDataStream()
}


const closeReportDataStream = () => {
  if (abortController) {
    abortController.abort()
  }
}

const getFileIds = (asset: Asset) => {
  if (asset.Kind.value?.Content.value instanceof AssetContent_Report_Studio_ApplicationStateSnapshot) {
    return [asset.Kind.value?.Content.value.ApplicationStateSnapshot!.ID]
  } else if (asset.Kind.value?.Content.value instanceof AssetContent_Report_Studio_Geometry) {
    return [asset.Kind.value?.Content.value.Geometry!.ID]
  } else if (asset.Kind.case === "Study") {
    const reportAsset = asset.Kind.value

    switch (reportAsset.Content.case) {
      case 'CBCT': {
        return reportAsset.Content.value.DICOMFiles!.map(file => file.ID)
      }
      case 'MeshModel': {
        return [reportAsset.Content.value.Geometry!.ID]
      }
      case "PanoramicXRay": {
        const value = reportAsset.Content.value
        return [value.PanoramaImage!.ID]
      }
      case "IntraoralXRay": {
        const value = reportAsset.Content.value
        return [value.Image!.ID]
      }
    }
  } else if (asset.Kind.case === "Report") {
    const reportAsset = asset.Kind.value

    switch (reportAsset.Content.case) {
      case 'RenderedDocument': {
        return [reportAsset.Content.value.Document!.ID]
      }
    }
  } else if (asset.Kind.case === "GeneratedReport") {
    const reportAsset = asset.Kind.value
    const reportAssetKind = reportAsset.Content.case

    switch (reportAssetKind) {
      case 'StudioCBCTDownscaled': {
        return [reportAsset.Content.value.Volume!.ID]
      }
      case "StudioPanoramaReformatGeneral": {
        const value = reportAsset.Content.value
        return [value.PanoramaSingle!.ID]
      }
      case "CBCTTile": {
        const value = reportAsset.Content.value
        return [value.Tile!.ID]
      }
      case "CBCTGPMPRSubVolume": {
        const value = reportAsset.Content.value
        return [value.Volume!.ID]
      }
      case "MeshModel": {
        const meshModel = reportAsset.Content.value
        let result: string[] = []
        switch (meshModel.Structure.case) {
          case "CBCTTooth": 
            if (meshModel.ToothNumerationWithSurfaceAttributes?.ID) result.push(meshModel.ToothNumerationWithSurfaceAttributes?.ID)
            if (meshModel.FusedModalityAttributes?.ID) result.push(meshModel.FusedModalityAttributes?.ID)
            result.push(meshModel.Mesh!.ID)
            return result
          case "CBCTAnatomy": 
            return [meshModel.Mesh!.ID]
          case "IOSTooth":
            if (meshModel.ToothNumerationWithSurfaceAttributes?.ID) result.push(meshModel.ToothNumerationWithSurfaceAttributes?.ID)
            if (meshModel.FusedModalityAttributes?.ID) result.push(meshModel.FusedModalityAttributes?.ID)
            result.push(meshModel.Mesh!.ID)
            return result
          case "IOSAnatomy":
            return [meshModel.Mesh!.ID]
          case "FusionTooth":
            if (meshModel.ToothNumerationWithSurfaceAttributes?.ID) result.push(meshModel.ToothNumerationWithSurfaceAttributes?.ID)
            if (meshModel.FusedModalityAttributes?.ID) result.push(meshModel.FusedModalityAttributes?.ID)
            result.push(meshModel.Mesh!.ID)
            return result
          case "CBCTToothPeriapicalRadiolucency":
            return [meshModel.Mesh!.ID]
          case "CBCTToothCondition":
            return [meshModel.Mesh!.ID]
          case "CBCTToothAnatomy":
            return [meshModel.Mesh!.ID]
          case "ArtificialCrown":
            return [meshModel.Mesh!.ID]
          default: 
            debug("[readReportAsset] unsupported MeshModel ", meshModel.Structure.case, meshModel)
            break
        }
        break
      }
      case "CBCTOrthoPDF": {
        const value = reportAsset.Content.value
        return [value.PDF!.ID]
      }
      case "MeshFaceSegmentation": {
        const value = reportAsset.Content.value
        return [value.FaceIndices!.ID]
      }
      case "IOXRayImage":{
        const value = reportAsset.Content.value
        return [value.Image!.ID]
      }
      case "CBCTGPPanoramaReformatGeneral": {
        const value = reportAsset.Content.value
        return [value.PanoramaSingle!.ID]
      }


      default: {
        debug("[readReportAsset] Unsupported asset type", reportAssetKind, reportAsset.Content)
        break
      }
    }
  }
}

const fillDownloadURL = async (Assets: AssetWithDownloadURLs[]) => {
  const urlData: { store: { [key: string]: string }, fileId: string, fileUrl: string }[] = []
  for (const asset of Assets) {
    for (const fileId of Object.keys(asset.DownloadURLs)) {
      const fileUrl = asset.DownloadURLs[fileId]
      urlData.push({ store: asset.DownloadURLs, fileId, fileUrl })
    }
  }
  const queueSize = 100
  while (urlData.length) {
    const data = urlData.splice(0, queueSize) // get portion and remove from array
    await Promise.all(data.map(d => unwrapAssetRequest(d.fileUrl).then(location => {
      d.store[d.fileId] = location
    })))
  }
}

export const unwrapAssetRequest = async (url: string, signal?: AbortSignal) => {
  const prefix = '?returnURL=true'
  const locationGetUrl = url.includes('#') ? url.replace('#', prefix + '#') : url + prefix
  const resp = await fetch(locationGetUrl, {
    method: 'GET',
    redirect: 'manual',
    credentials: CORS_POLICY,
    signal
  })
  const location = await resp.text()
  return location
}

export const renderDisplayImageReqBody = async () => {
  const GLOBAL_COLLECTOR = await runReportDataStream()
  closeReportDataStream()
  if (!GLOBAL_COLLECTOR) return

  await fillDownloadURL(GLOBAL_COLLECTOR.Assets)

  const requestBody = new RenderDisplayImageReq({
    Input: {
      case: 'Report',
      value: {
        Assets: GLOBAL_COLLECTOR.Assets,
        Report: new ReportBundle({
          Conditions: GLOBAL_COLLECTOR.Conditions,
          Teeth: GLOBAL_COLLECTOR.Teeth,
          Report: GLOBAL_COLLECTOR.Report,
          ToothLandmarks: GLOBAL_COLLECTOR.TeethLandmarks,
        }),
      }
    },
    FeatureFlags: GLOBAL_COLLECTOR.Organization.FeatureFlags,
    UploadURL: 'http://localhost:8080/upload',
    OutputWidth: 800,
    OutputHeight: 500
  })

  return requestBody
}

export const openFileInNewTab = (data: Uint8Array, type: string = 'application/pdf') => {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
};

export const gpReportPreviewHandler = async (isRequestOnBack: boolean = false) => {

  if (isRequestOnBack) {

    throw new Error("not implemented")

  } else {
    const requestBody = await renderDisplayImageReqBody();
    if (!requestBody) {
      debug('Request body is not defined');
      return;
    }

    const transport = createConnectTransport({
      baseUrl: 'http://localhost:3000',
    });

    const client = createClient(RendererService, transport);
    const response = await client.renderDisplayImage_Debug(requestBody);
    debug('response=', response);

    openFileInNewTab(response.OutputData, 'image/webp');
  }
};

window.preview = () => gpReportPreviewHandler()

export const rawMeshStudyPreviewReqBody = async (studyID: string) => {
  const { Study, StudyAssets } = await api.study.getStudyViewerData({
    StudyID: studyID,
  })

  if (!Study) {
    debug('[previewRawMesh] study is not defined')
    return
  }

  const Assets: AssetWithDownloadURLs[] = StudyAssets.map((asset) => {
    const DownloadURLs: { [key: string]: string } = {}

    getFileIds(asset)?.forEach((fileID) => {
      DownloadURLs[fileID] = getFileSrc(fileID)
    })

    return { Asset: asset, DownloadURLs } as AssetWithDownloadURLs
  })

  await fillDownloadURL(Assets)

  return new RenderDisplayImageReq({
    Input: {
      case: 'RawMeshStudy',
      value: { Assets, Study },
    },
    UploadURL: 'http://localhost:8080/upload',
    OutputWidth: 800,
    OutputHeight: 500,
  })
}

export const rawMeshStudyPreviewHandler = async (studyID: string) => {
  const requestBody = await rawMeshStudyPreviewReqBody(studyID)

  if (!requestBody) {
    debug('Request body is not defined')
    return
  }

  const transport = createConnectTransport({
    baseUrl: 'http://localhost:3000',
  })

  const client = createClient(RendererService, transport)
  const response = await client.renderDisplayImage_Debug(requestBody)
  debug('response=', response)

  openFileInNewTab(response.OutputData, 'image/webp')
}

window.previewRawMesh = (studyID: string) => rawMeshStudyPreviewHandler(studyID)

